import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleReload = () => {
    // Reload the entire app
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-content">
            <div className="error-icon">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#dc2626"
                  strokeWidth="2"
                />
                <line
                  x1="15"
                  y1="9"
                  x2="9"
                  y2="15"
                  stroke="#dc2626"
                  strokeWidth="2"
                />
                <line
                  x1="9"
                  y1="9"
                  x2="15"
                  y2="15"
                  stroke="#dc2626"
                  strokeWidth="2"
                />
              </svg>
            </div>

            <h1 className="error-title">Something went wrong</h1>
            <p className="error-message">
              An unexpected error occurred in the Sales Bot application. Please
              try reloading the app or contact support if the problem persists.
            </p>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="error-details">
                <summary>Error Details (Development Mode)</summary>
                <pre className="error-stack">
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="error-actions">
              <button onClick={this.handleReload} className="reload-button">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 4V10H7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M23 20V14H17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M3.51 15A9 9 0 0 0 18.36 18.36L23 14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Reload Application
              </button>
            </div>
          </div>

          <style jsx>{`
            .error-boundary {
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #f8fafc;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
                sans-serif;
              padding: 20px;
            }

            .error-content {
              background: white;
              border-radius: 12px;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
              padding: 40px;
              max-width: 600px;
              width: 100%;
              text-align: center;
            }

            .error-icon {
              margin-bottom: 24px;
            }

            .error-title {
              font-size: 24px;
              font-weight: 600;
              color: #111827;
              margin: 0 0 16px 0;
            }

            .error-message {
              font-size: 16px;
              color: #6b7280;
              line-height: 1.5;
              margin: 0 0 32px 0;
            }

            .error-details {
              text-align: left;
              margin: 24px 0;
              padding: 16px;
              background: #f3f4f6;
              border-radius: 8px;
              border: 1px solid #e5e7eb;
            }

            .error-details summary {
              cursor: pointer;
              font-weight: 500;
              color: #374151;
              margin-bottom: 12px;
            }

            .error-stack {
              font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
              font-size: 12px;
              color: #dc2626;
              background: white;
              padding: 12px;
              border-radius: 4px;
              border: 1px solid #fecaca;
              overflow-x: auto;
              white-space: pre-wrap;
              margin: 0;
            }

            .error-actions {
              display: flex;
              justify-content: center;
              gap: 12px;
            }

            .reload-button {
              display: flex;
              align-items: center;
              gap: 8px;
              background: #3b82f6;
              color: white;
              border: none;
              border-radius: 8px;
              padding: 12px 24px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              transition: background-color 0.2s ease;
            }

            .reload-button:hover {
              background: #2563eb;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
